import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { FormikProps, useFormik } from "formik";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { Alert } from "@mui/material";
import {
  UpdateUserAuthoritiesRequest,
  UpdateUserRequest,
  UserResponse,
  UserRoleType,
} from "../../../../../generated/user-api";
import UserFields from "../../../Components/UserFields/UserFields";
import {
  useCustomerFeatures,
  useCustomerId,
} from "../../../../../Providers/CustomerProvider/CustomerProvider";
import { useCustomerCurrentUpdate } from "../../../../../Providers/RecentUpdatesProvider/RecentUpdatesProvider";
import { messages } from "../../ModifyUser.messages";
import AuthorityFields from "../../../Components/AuthorityFields/AuthorityFields";
import { displayUser } from "../../../../../Utils/User";
import { userService } from "../../../../../Providers/ServiceProvider/ServiceProvider";
import UpdateUserValidationSchema from "../../../Components/Validation/UpdateUserValidationSchema";
import { User } from "../../../../../Models/User";
import {
  emptyStringToUndefined,
  formatHSAID,
} from "../../../../../Utils/Format";
import { hasFeatureEnabled } from "../../../../FeatureController/FeatureController";
import { CustomerFeatureType } from "../../../../../generated/customersettings-api";
import { useStyles } from "./General.style";
import { isContentEqual } from "../../../../../Utils/Helper";

const mapUserObject = (user: UserResponse) => {
  return {
    firstName: emptyStringToUndefined(user.firstName),
    lastName: emptyStringToUndefined(user.lastName),
    workTitle: emptyStringToUndefined(user.workTitle),
    email: emptyStringToUndefined(user.email),
    mobilePhoneNumber: emptyStringToUndefined(user.mobilePhoneNumber),
    additionalInformation: emptyStringToUndefined(user.additionalInformation),
  } as UpdateUserRequest;
};

const mapAuthoritiesObject = (user: UserResponse) => {
  return {
    userAuthorities: user.userAuthorities,
  } as UpdateUserAuthoritiesRequest;
};

interface MessageObject {
  id: string;
  defaultMessage: string;
  description: string;
}

interface Props {
  onClose: () => void;
  onUpdated?: (user: UserResponse) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  user: UserResponse;
  submitLabel?: string;
  contentText?: MessageObject;
}

const General = (props: Props) => {
  const {
    user,
    onClose,
    onUpdated,
    submitLabel,
    contentText,
    onDirtyChange,
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const customerId = useCustomerId();
  const [customerupdates, setCustomerupdates] = useCustomerCurrentUpdate();
  const [personNumber, setPersonNumber] = useState<string>();
  const [hsaId, setHsaId] = useState<string>();
  const [duplicateHsaIdError, setDuplicateHsaIdError] = useState(false);
  const customerFeatures = useCustomerFeatures();
  const doFormatPersonNumber = hasFeatureEnabled(
    CustomerFeatureType.UsesPersonNumber,
    customerFeatures
  );

  const loadPersonNumber = () => {
    if (user && customerId) {
      userService()
        .getPersonNumber({ userId: user.id })
        .then((res) => setPersonNumber(res.personNumber));
    }
  };

  const loadUserHsaId = () => {
    if (user) {
      userService()
        .getHsaId({ userId: user.id })
        .then((res) => setHsaId(res.hsaId ? res.hsaId : ""));
    }
  };

  const initialValues: User = {
    ...user,
    hsaId,
    personNumber,
    mobilePhoneNumber: user.mobilePhoneNumber,
  };

  const updateUser = (formik: FormikProps<User>, values: User) => {
    if (customerId && user) {
      userService()
        .updateUser({
          customerId,
          userId: user.id,
          body: mapUserObject(values),
        })
        .then((res) => {
          if (!isContentEqual(values.userAuthorities, res.userAuthorities)) {
            return userService()
              .updateUserAuthorities({
                customerId,
                userId: user.id,
                body: mapAuthoritiesObject(values),
              })
              .then(
                (userAuthorities) =>
                  ({
                    ...res,
                    userAuthorities: userAuthorities.userAuthorities,
                  } as UserResponse)
              );
          }
          return res;
        })
        .then((res) => {
          if (onUpdated) {
            onUpdated(res);
          }
          const arrayCopy: UserResponse[] = customerupdates.userResponse.filter(
            (element: UserResponse) => {
              return element.id !== user.id;
            }
          );
          arrayCopy.push(res);
          setCustomerupdates({ userResponse: arrayCopy });
          enqueueSnackbar(
            intl.formatMessage(messages.success, {
              name: displayUser(doFormatPersonNumber, res, true),
            }),
            { variant: "success" }
          );
        })
        .catch(() => {
          enqueueSnackbar(intl.formatMessage(messages.generalError), {
            variant: "error",
          });
        })
        .finally(onClose);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: UpdateUserValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (customerId && user) {
        if (values.hsaId !== undefined && hsaId !== values.hsaId) {
          userService()
            .updateHsaId({
              userId: user.id,
              updateUserHsaIdRequest: {
                hsaId: formatHSAID(emptyStringToUndefined(values.hsaId)),
              },
            })
            .then((result) => setHsaId(result.hsaId || ""))
            .then(() => updateUser(formik, values))
            .catch((error) => {
              if (error.status === 409) {
                setDuplicateHsaIdError(true);
              }
            });
        } else {
          updateUser(formik, values);
        }
      }
    },
  });

  useEffect(() => {
    if (!formik.errors.hsaId) {
      setDuplicateHsaIdError(false);
    }
  }, [formik.errors.hsaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onDirtyChange) {
      const dirtyFields = Object.keys(formik.values).filter((key) =>
        key !== "userAuthorities"
          ? formik.values[key as keyof typeof formik.values] !==
            formik.initialValues[key as keyof typeof formik.initialValues]
          : false
      );
      onDirtyChange(dirtyFields.length > 0);
    }
  }, [formik.dirty, formik.values]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogContent>
        {contentText && (
          <Alert severity="info" className={classes.contentText}>
            {intl.formatMessage(contentText)}
          </Alert>
        )}
        <UserFields
          formik={formik}
          useHsaIdLock
          loadHsaId={loadUserHsaId}
          duplicateHsaIdError={duplicateHsaIdError}
          usePersonNumberLock
          loadPersonNumber={loadPersonNumber}
          userRole={user.userRole}
          disabledFields={["personNumber"]}
          hideUsername={!user.hasActivatedPassword}
        />
        <AuthorityFields formik={formik} userRole={user.userRole} user={user} />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          disabled={
            formik.values.userAuthorities?.length === 0 &&
            user.userRole !== UserRoleType.PlatformAdmin
          }
        >
          {submitLabel || intl.formatMessage(messages.submitButtonLabel)}
        </Button>
        <Button onClick={onClose} color="primary">
          {intl.formatMessage(messages.cancelButtonLabel)}
        </Button>
      </DialogActions>
    </form>
  );
};

export default General;
